export const PARTICLE_BLE_WIFI_SERVICE_UUID = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
export const PARTICLE_BLE_LISTENING_MODE_SERVICE_UUID = '01d7bc00-4456-11e3-87e2-0800200c9a66';
export const PARTICLE_BLE_WIFI_TX_CHARACTERISTIC_UUID = '6e400002-b5a3-f393-e0a9-e50e24dcca9e';
export const PARTICLE_BLE_WIFI_RX_CHARACTERISTIC_UUID = '6e400003-b5a3-f393-e0a9-e50e24dcca9e';
export const PARTICLE_BLE_NAME_PREFIX = 'ShefaGreen-';

export const WIFI_SECURITY_WPA2 = 3;
export const WIFI_REBOOT_COMMAND_TYPE = 4;
export const WIFI_SETUP_LAST_SSID_KEY = 'shefa:wifiSetup:lastSsid';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function assertByteLength(label, bytes) {
  if (bytes.length > 255) {
    throw new Error(`${label} is too long for Particle BLE Wi-Fi setup payload`);
  }
}

export function buildWifiCredentialsPayload(ssid, password) {
  const ssidBytes = encoder.encode(String(ssid || '').trim());
  const passwordBytes = encoder.encode(String(password || ''));

  if (!ssidBytes.length) {
    throw new Error('SSID is required');
  }

  assertByteLength('SSID', ssidBytes);
  assertByteLength('Password', passwordBytes);

  const payload = new Uint8Array(3 + ssidBytes.length + passwordBytes.length);
  let offset = 0;
  payload[offset++] = WIFI_SECURITY_WPA2;
  payload[offset++] = ssidBytes.length;
  payload.set(ssidBytes, offset);
  offset += ssidBytes.length;
  payload[offset++] = passwordBytes.length;
  payload.set(passwordBytes, offset);

  return payload;
}

export function buildWifiRebootPayload() {
  return new Uint8Array([WIFI_REBOOT_COMMAND_TYPE]);
}

export function formatBlePayload(value) {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value || []);
  return Array.from(bytes)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join(' ');
}

export function getParticleBleRequestOptions() {
  return {
    filters: [{ services: [PARTICLE_BLE_LISTENING_MODE_SERVICE_UUID] }],
    optionalServices: [PARTICLE_BLE_WIFI_SERVICE_UUID],
  };
}

export class ParticleBleWifiClient {
  constructor({ onNotification, onDisconnected } = {}) {
    this.device = null;
    this.server = null;
    this.txCharacteristic = null;
    this.rxCharacteristic = null;
    this.onNotification = onNotification;
    this.onDisconnected = onDisconnected;
    this.handleNotification = this.handleNotification.bind(this);
    this.handleDisconnected = this.handleDisconnected.bind(this);
  }

  get connected() {
    return Boolean(this.device?.gatt?.connected && this.txCharacteristic);
  }

  async connect() {
    if (!navigator.bluetooth) {
      throw new Error('Web Bluetooth is not available in this browser');
    }

    const options = getParticleBleRequestOptions();
    console.log('Starting Particle Listening Mode scan...', options);
    this.device = await navigator.bluetooth.requestDevice(options);
    if (!this.device.name) {
      console.log('Найдено потенциальное устройство Particle, подключаюсь для проверки...');
    }
    console.log('> Name: ' + this.device.name);
    console.log('> Id: ' + this.device.id);
    console.log('> Device:', this.device);
    console.log('> adData:', this.device.adData || this.device.advertisementData || null);
    this.device.addEventListener('gattserverdisconnected', this.handleDisconnected);

    this.server = await this.device.gatt.connect();
    const service = await this.server.getPrimaryService(PARTICLE_BLE_WIFI_SERVICE_UUID);
    this.txCharacteristic = await service.getCharacteristic(PARTICLE_BLE_WIFI_TX_CHARACTERISTIC_UUID);
    this.rxCharacteristic = await service.getCharacteristic(PARTICLE_BLE_WIFI_RX_CHARACTERISTIC_UUID);
    await this.rxCharacteristic.startNotifications();
    this.rxCharacteristic.addEventListener('characteristicvaluechanged', this.handleNotification);

    return this.device;
  }

  async setWifiCredentials(ssid, password) {
    if (!this.connected) {
      await this.connect();
    }

    const credentialsPayload = buildWifiCredentialsPayload(ssid, password);
    await this.write(credentialsPayload);

    const rebootPayload = buildWifiRebootPayload();
    await this.write(rebootPayload);

    return { credentialsPayload, rebootPayload };
  }

  async write(payload) {
    if (!this.txCharacteristic) {
      throw new Error('BLE device is not connected');
    }

    if (typeof this.txCharacteristic.writeValueWithoutResponse === 'function') {
      await this.txCharacteristic.writeValueWithoutResponse(payload);
      return;
    }

    await this.txCharacteristic.writeValue(payload);
  }

  disconnect() {
    if (this.rxCharacteristic) {
      this.rxCharacteristic.removeEventListener('characteristicvaluechanged', this.handleNotification);
    }
    if (this.device) {
      this.device.removeEventListener('gattserverdisconnected', this.handleDisconnected);
      if (this.device.gatt?.connected) {
        this.device.gatt.disconnect();
      }
    }
    this.device = null;
    this.server = null;
    this.txCharacteristic = null;
    this.rxCharacteristic = null;
  }

  handleNotification(event) {
    const value = event.target?.value;
    if (!value || !this.onNotification) return;
    const bytes = new Uint8Array(value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength));
    this.onNotification({
      bytes,
      hex: formatBlePayload(bytes),
      text: decoder.decode(bytes),
    });
  }

  handleDisconnected() {
    this.txCharacteristic = null;
    this.rxCharacteristic = null;
    this.server = null;
    if (this.onDisconnected) {
      this.onDisconnected();
    }
  }
}
